import type { JSX, ReactNode } from 'react';

import { Box, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { AppTheme } from '@/theme/theme';

// Shared Vapas status chip (DESIGN.md §6.4 `.dot` variant + §2 status palettes).
// THE status chip for the whole app — orders here, admin/audit in Phase 2b
// reuse it verbatim. It reads `theme.statusPalette[status]` (bg + color pair,
// raw-hex-exempt per DESIGN.md §2 but theme-housed), renders the 6px
// `currentColor` dot via a `::before`, and pulls its Czech label from the
// `common:status.*` namespace by default. Callers that need a context-specific
// caption (e.g. orders' derived "Vše přiřazeno") pass `label` to override the
// copy while keeping the palette mapping.

export type StatusKey = keyof AppTheme['statusPalette'];

export interface StatusChipProps {
  // Palette key (DESIGN.md §2 status rows). Drives both colour and — unless
  // `label` is given — the default `common:status.<status>` copy.
  status: StatusKey;
  // Optional copy override (already-translated). Use for derived statuses whose
  // label differs from the canonical status-row name but reuses its palette.
  label?: ReactNode;
  // `dot` (default) renders the §6.4 leading dot; `plain` omits it.
  variant?: 'dot' | 'plain';
  // Forwarded for layout (e.g. `ml: 'auto'`); kept minimal on purpose.
  className?: string;
  'data-testid'?: string;
}

const DOT_SIZE_PX = 6;

export const StatusChip = ({
  status,
  label,
  variant = 'dot',
  className,
  'data-testid': dataTestId,
}: StatusChipProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation('common');
  const pair = theme.statusPalette[status];
  const text = label ?? t(`status.${status}`);

  return (
    <Box
      component="span"
      className={className}
      data-testid={dataTestId}
      data-status={status}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75, // 6px (DESIGN.md §6.4)
        px: 1.25, // 10px
        py: 0.375, // 3px
        borderRadius: 999,
        fontSize: 11.5,
        fontWeight: 500,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        bgcolor: pair.bg,
        color: pair.color,
      }}
    >
      {variant === 'dot' ? (
        <Box
          component="span"
          aria-hidden
          sx={{
            width: DOT_SIZE_PX,
            height: DOT_SIZE_PX,
            borderRadius: 999,
            bgcolor: 'currentColor',
            flexShrink: 0,
          }}
        />
      ) : null}
      {text}
    </Box>
  );
};
